/**
 * Source→client identity text-pair construction, shared by the server-side
 * brand-skin pipeline and the client-side composition-skin (post-insert
 * re-skin). Extracted from brand-skin.js so browser code can import it
 * without dragging in css-tree.
 *
 * Worker-safe: pure string functions.
 */

import { applyOccurrences } from './occurrence-engine.js';
import { isPlausibleBusinessName } from './identity-defects.js';
import { ADDRESS_ROLES, parseAddressParts, partsFromClientInfoLines } from './address-parts.js';

function clean(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function unique(values) {
  return [...new Set(values.map(clean).filter(Boolean))];
}

export function normalizeEmail(value) {
  return clean(value)
    .replace(/^mailto:/i, '')
    .split('?')[0]
    .trim();
}

export function splitAddress(address) {
  const raw = clean(address);
  if (!raw) return [];
  return unique(raw.split(/\n| {2,}/g).flatMap((part) => part.split(/\s*,\s*/g)));
}

/**
 * Ordered [from, to] literal replacement pairs for the source business's
 * identity strings. Missing client values produce DELETION pairs (to: '')
 * rather than leaving source identity in place; surplus source address lines
 * are deleted rather than duplicated.
 */
export function makeTextPairs(sourceProfile = {}, clientProfile = {}) {
  const pairs = [];
  const skipped = [];
  // `businessName` and `businessNameVariants[0]` are routinely the same string,
  // and both a decline and a pair would otherwise be recorded twice. The wizard
  // renders `skipped` one row per entry.
  const seenPairs = new Set();
  const seenSkips = new Set();
  const add = (source, target, kind = 'name', mode = 'word') => {
    const from = clean(source);
    const to = clean(target);
    if (!from) return;
    if (from === to) {
      // NOT an omission. The client is in the same city, or trades under the
      // same word — there is nothing to change, and the report should say so
      // rather than leave a silent gap. This case also used to hard-fail a
      // compose: `balancedskin` x `amitakalramd` are both in Newport Beach, and
      // no pair could remove a term the scanner then blocked on.
      pushSkip({ field: kind, reason: `client and template share "${from}" — nothing to change` });
      return;
    }
    if (!to) return;
    pushPair({ from, to, kind, mode, caseInsensitive: true });
  };

  // SK-3. A source name that is a nav word, an SEO phrase or a fragment of the
  // domain is NOT an identity, and substituting it is worse than leaving it:
  // `bluffcitywebdesigns` stored `"About"`, so every "About" in the template —
  // nav items, headings, footer sitemap — became the client's business name.
  // Leaving the word alone and SAYING SO is the better outcome.
  const pushPair = (rule) => {
    const key = `${rule.kind}\u0000${rule.mode}\u0000${rule.from}\u0000${rule.to}`;
    if (seenPairs.has(key)) return;
    seenPairs.add(key);
    pairs.push(rule);
  };
  const pushSkip = (entry) => {
    const key = `${entry.field}\u0000${entry.reason}`;
    if (seenSkips.has(key)) return;
    seenSkips.add(key);
    skipped.push(entry);
  };

  const nameCandidates = [
    sourceProfile.businessName,
    ...(sourceProfile.businessNameVariants || [])
  ];
  for (const candidate of nameCandidates) {
    const from = clean(candidate);
    if (!from) continue;
    const isScoredPrimary = from === clean(sourceProfile.businessName);
    if (
      !isPlausibleBusinessName(from, {
        sourceDomain: sourceProfile.sourceDomain,
        nameConfidence: isScoredPrimary ? sourceProfile.nameConfidence : '',
        nameSource: isScoredPrimary ? sourceProfile.nameSource : ''
      })
    ) {
      pushSkip({
        field: 'businessName',
        reason: `source name ${JSON.stringify(from)} is a generic page word — not substituted`
      });
      continue;
    }
    add(from, clientProfile.businessName, 'businessName');
  }

  const clientPhone = clean(clientProfile.phone);
  // ONE phone rule per distinct number, matched on digits. Enumerating string
  // variants is unsound: `barbalandscapedesign` listed `310-877 4008` and
  // `(310) 877-4008`, and the page shipped `(310) 877 4008`.
  const phoneSources = unique([
    sourceProfile.phone,
    ...(Array.isArray(sourceProfile.phoneVariants) ? sourceProfile.phoneVariants : [])
  ]);
  const seenPhones = new Set();
  for (const variant of phoneSources) {
    const digits = String(variant).replace(/\D+/g, '');
    if (digits.length < 7 || seenPhones.has(digits)) continue;
    seenPhones.add(digits);
    if (clientPhone) add(variant, clientPhone, 'phone', 'phone');
    // No client phone: the source number must still not survive — delete it
    // (mirrors the email deletion path; the link policy flags tel: links).
    else pushPair({ from: variant, to: '', kind: 'phone', mode: 'phone', caseInsensitive: true });
  }

  const sourceEmail = clean(sourceProfile.email);
  const normalizedSourceEmail = normalizeEmail(sourceProfile.email);
  const clientEmail = clean(clientProfile.email);
  if (clientEmail) {
    add(sourceEmail, clientEmail, 'email', 'exact');
    add(normalizedSourceEmail, clientEmail, 'email', 'exact');
  } else if (sourceEmail) {
    pushPair({ from: sourceEmail, to: '', kind: 'email', mode: 'exact', caseInsensitive: true });
    if (normalizedSourceEmail !== sourceEmail) {
      pushPair({
        from: normalizedSourceEmail,
        to: '',
        kind: 'email',
        mode: 'exact',
        caseInsensitive: true
      });
    }
  }

  // ---- address -----------------------------------------------------------
  // Resolution order. Only the first two map by ROLE; the third is the legacy
  // positional zip, kept ONLY so a profile that predates SK-5 behaves exactly
  // as before rather than changing under a user who has not regenerated.
  //
  //   1. sourceProfile.addressParts        — role-tagged by the extractor
  //   2. the client-info FIVE-SLOT shape   — role-tagged by position
  //   3. addressLines                      — positional zip (legacy)
  //
  // The CLIENT side is always re-derived from `clientProfile.address`, never
  // trusted from the wire: applySkin receives a user-edited profile, and a
  // stale `addressParts` echoed back by the browser must not override a city
  // the user just corrected.
  const hasStructuredAddress = Object.prototype.hasOwnProperty.call(sourceProfile, 'addressParts');
  const sourceParts =
    (sourceProfile.addressParts && typeof sourceProfile.addressParts === 'object'
      ? sourceProfile.addressParts
      : null) ||
    (!hasStructuredAddress ? partsFromClientInfoLines(sourceProfile.addressLines) : null);

  const clientParsed = parseAddressParts(clientProfile.address);

  if (sourceParts && clientParsed.parts) {
    for (const role of ADDRESS_ROLES) {
      const from = clean(sourceParts[role]);
      const to = clean(clientParsed.parts[role]);
      if (!from) continue;
      if (to) add(from, to, 'address');
      // A source role the client does not have is DELETED, not left standing —
      // a template's street address surviving under a client's name is worse
      // than an absent one.
      else pushPair({ from, to: '', kind: 'address', mode: 'word', caseInsensitive: true });
    }
    // Region variants: the page may say `TN` where the profile stored `US-TN`.
    for (const variant of sourceProfile.regionVariants || []) {
      const from = clean(variant);
      const to = clean(clientParsed.parts.county);
      if (from && to) add(from, to, 'address');
    }
  } else if (sourceParts && !clientParsed.parts && clean(clientProfile.address)) {
    // We know the source's roles but cannot establish the client's. Guessing is
    // what produced "Everett, FL" and "Arlington, TN": say so and change
    // nothing.
    pushSkip({
      field: 'address',
      reason: `could not read the client address into parts (${clientParsed.reason}) — the template's address was left alone`
    });
  } else if (hasStructuredAddress) {
    // A regenerated profile explicitly carrying addressParts:null is not a
    // legacy profile. The extractor looked and could not establish roles, so
    // positional fallback would re-create fabricated hybrids such as
    // "Everett, FL". Preserve the source address and disclose the ambiguity.
    if ((sourceProfile.addressLines || []).length || clean(sourceProfile.address)) {
      pushSkip({
        field: 'address',
        reason: `the template address has no reliable parts (${sourceProfile.addressConfidence || 'low'} confidence) — it was left alone`
      });
    }
  } else {
    const sourceAddress = sourceProfile.addressLines || splitAddress(sourceProfile.address);
    const targetAddress = splitAddress(clientProfile.address);
    for (let i = 0; i < sourceAddress.length; i++) {
      if (targetAddress[i]) {
        add(sourceAddress[i], targetAddress[i], 'address');
      } else if (i === 0 && clean(clientProfile.address)) {
        add(sourceAddress[i], clientProfile.address, 'address');
      } else {
        // Extra source address lines with no client counterpart are deleted —
        // duplicating the client's full address into every surplus line reads
        // as a bug in the mockup.
        const from = clean(sourceAddress[i]);
        if (from) pushPair({ from, to: '', kind: 'address', mode: 'word', caseInsensitive: true });
      }
    }
  }

  return { pairs, skipped };
}

/**
 * Apply pairs through the shared engine.
 *
 * Accepts BOTH the typed `{from,to,kind,mode}` shape and the legacy
 * `[from, to]` tuple, because `composition-skin.js` and several suites still
 * build tuples by hand. A tuple is treated as a case-insensitive word pair,
 * which is the safe reading — the old behaviour was case-SENSITIVE substring,
 * and that is precisely what let `Abshillbillygyros` miss 233 lowercase
 * occurrences while `Gyros` fired inside ordinary prose.
 */
export function replaceTextWithPairs(value, pairs) {
  return replaceOccurrencesWithPairs(value, pairs).text;
}

/**
 * The same replacement, keeping the COUNT the engine already computed.
 *
 * `applyOccurrences` has returned `replaced` since SK-3 and every caller threw
 * it away, which is why `importReport.applied.textPairs` was a hard-coded 0 in
 * every report the product has ever rendered.
 */
export function replaceOccurrencesWithPairs(value, pairs) {
  const rules = (pairs || []).map((pair) =>
    Array.isArray(pair) ? { from: pair[0], to: pair[1], mode: 'word', caseInsensitive: true } : pair
  );
  return applyOccurrences(String(value ?? ''), rules);
}
